import {
  GRID_ROW_NUMBER_COL_DEF,
  type GridFilterModel,
  type GridPinnedColumnFields,
  type GridSortModel,
} from '@repo/go-table';
import { action, computed, makeObservable, observable } from 'mobx';

import type { SettingsStore, TTableSettingsModel } from '../../stores/SettingsStore';

import type { AlertRulesTableUiState } from './AlertRulesTableUiState';

export const FACT_HIDDEN_COLUMNS = [];
export const SERVICE_COLUMNS = [GRID_ROW_NUMBER_COL_DEF.field];
export const PLAN_HIDDEN_COLUMNS = [];
export const MIN_VISIBLE_COLUMNS = 3;
export const READ_MODE_HIDDEN_COLUMNS = [];

export class DisplayManager {
  @observable private _settings: TTableSettingsModel;

  constructor(
    private readonly settingsStore: SettingsStore,
    private readonly index: 'time' | 'depth',
    private readonly uiState: AlertRulesTableUiState,
  ) {
    const sourceSettings = settingsStore.getAlertRulesSettings(index);
    this._settings = {
      columnsOrderModel: sourceSettings.columnsOrderModel ?? [],
      columnsVisibility: sourceSettings.columnsVisibility ?? {},
      columnsWidth: sourceSettings.columnsWidth ?? {},
      sortingModel: sourceSettings.sortingModel ?? [],
      filterModel: sourceSettings.filterModel ?? { items: [] },
      pinnedColumns: sourceSettings.pinnedColumns,
    };
    makeObservable(this);
  }

  @computed
  get settings(): TTableSettingsModel {
    return this._settings;
  }

  @computed
  get columnsVisibilityModel(): Record<string, boolean> {
    return this.uiState.editing ? {} : (this._settings.columnsVisibility ?? {});
  }

  @computed
  get sortingModel(): GridSortModel {
    return this._settings.sortingModel ?? [];
  }

  @computed
  get filterModel(): GridFilterModel {
    return this._settings.filterModel ?? { items: [] };
  }

  @computed
  get columnsOrderModel(): string[] {
    return this._settings.columnsOrderModel ?? [];
  }

  @computed
  get pinnedColumns(): GridPinnedColumnFields | undefined {
    return this._settings.pinnedColumns;
  }

  getColumnsWidth(): Record<string, number> | undefined {
    return this._settings.columnsWidth;
  }

  getColumnsOrderModel(): string[] {
    return this._settings.columnsOrderModel ?? [];
  }

  @action.bound
  private async updateSettings(newSettings: TTableSettingsModel) {
    this._settings = newSettings;
    this.settingsStore.setAlertRulesSettings(this.index, newSettings);
  }

  @action.bound
  onColumnsOrderModelChange(columns: string[]): void {
    const newSettings = { ...this._settings, columnsOrderModel: columns };

    this.updateSettings(newSettings);
  }

  @action.bound
  onSortChange(model: TTableSettingsModel['sortingModel']): void {
    const newSettings = { ...this._settings, sortingModel: model };

    this.updateSettings(newSettings);
  }

  @action.bound
  onFilterChange(model: TTableSettingsModel['filterModel']): void {
    const newSettings = { ...this._settings, filterModel: model };

    this.updateSettings(newSettings);
  }

  @action.bound
  onPinChange(pinnedColumns: TTableSettingsModel['pinnedColumns']): void {
    const newSettings = { ...this._settings, pinnedColumns };

    this.updateSettings(newSettings);
  }

  @action.bound
  onWidthChange(incomeColumnsWidth: Record<string, number>): void {
    const newWidthSettings: Record<string, number> = { ...this._settings.columnsWidth, ...incomeColumnsWidth };
    const newSettings = { ...this._settings, columnsWidth: newWidthSettings };

    this.updateSettings(newSettings);
  }

  @action.bound
  onColumnVisibilityChange(model: Record<string, boolean>): void {
    const newSettings = { ...this._settings, columnsVisibility: model };

    this.updateSettings(newSettings);
  }
}
